/**
 * WEB-597 — shared clipboard + toast helper (U1).
 *
 * Centralizes the `navigator.clipboard.writeText` + success/error toast that
 * four surfaces previously duplicated. Takes `showNotification` as a callback
 * (does not import `useNotification`) so this module stays pure / testable (R9).
 */

import type { NotificationInput } from '@loci/store';

/** Toast callback shape — mirrors `useNotification().showNotification`. */
export type ShowNotification = (input: NotificationInput) => void;

/** Fire a toast; swallow callback failures so `copyLink` never rejects. */
function notifySafely(showNotification: ShowNotification, input: NotificationInput): void {
  try {
    showNotification(input);
  } catch {
    // Toast hosts must not break the never-throws contract.
  }
}

/**
 * Copy `url` to the clipboard and toast the outcome.
 *
 * Success → `{ type: 'success', title: 'Link copied!' }`.
 * Failure → `{ type: 'error', title: "Couldn't copy link" }` (AE5).
 * Never throws — callers can await without a try/catch. Resolves `true` when
 * the clipboard write succeeded and `false` when it failed (WEB-603), so
 * callers can tell the two apart; a throwing toast does not change the result.
 */
export async function copyLink(url: string, showNotification: ShowNotification): Promise<boolean> {
  // Scope try/catch to the clipboard write only — a throwing success toast
  // must not be misclassified as a clipboard failure.
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    notifySafely(showNotification, { type: 'error', title: "Couldn't copy link" });
    return false;
  }
  notifySafely(showNotification, { type: 'success', title: 'Link copied!' });
  return true;
}
